import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { articles, type Article } from '@/mocks/articles';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);

  useEffect(() => {
    const found = articles.find((a) => a.slug === slug) || null;
    setArticle(found);
  }, [slug]);

  const relatedArticles = article
    ? articles
        .filter((a) => a.category.slug === article.category.slug && a.id !== article.id)
        .slice(0, 3)
    : [];

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到" description="该文章不存在或已被移除。" />
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <p className="text-foreground-500">文章未找到</p>
          <Link to="/" className="text-primary-500 text-sm mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const articleJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.excerpt,
      "author": {
        "@type": "Person",
        "name": article.author,
      },
      "datePublished": article.publishDate,
      "image": article.image,
      "publisher": {
        "@type": "Organization",
        "name": "91网",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "首页",
          "item": "https://sddiweite.cn",
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": article.category.name,
          "item": `https://sddiweite.cn/category/${article.category.slug}`,
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": article.title,
        },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://sddiweite.cn/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        jsonLd={articleJsonLd}
      />
      <Navbar />
      <main className="pt-24 pb-20">
        <article className="mx-auto px-6" style={{ maxWidth: 800 }}>
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category.name, href: `/category/${article.category.slug}` },
              { label: article.title },
            ]}
          />

          <header className="mt-8 mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.category.slug}`}
                className="text-xs text-primary-600 bg-primary-100 px-3 py-1 rounded-full hover:bg-primary-200 transition-colors"
              >
                {article.category.name}
              </Link>
              <span className="text-xs text-foreground-400">{article.publishDate}</span>
              <span className="text-xs text-foreground-400">{article.readTime}阅读</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-sm text-foreground-500 leading-relaxed">{article.excerpt}</p>
            <div className="flex items-center gap-2 mt-4">
              <span className="text-xs text-foreground-400">作者：{article.author}</span>
            </div>
          </header>

          <div className="relative w-full h-[400px] rounded-xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose-content text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-background-200">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs text-secondary-600 bg-secondary-100 px-3 py-1 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>

          {relatedArticles.length > 0 && (
            <section className="mt-14">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-2">{ra.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}